import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useAppContext, ACTION_TYPES } from '../../Context/AppContext';
import { signupUser, clearLocalAuth } from '../../Shared/Api';
import { EmailMockup } from '../Landing/ProductMockups';
import { getAuthenticatedHomePath } from '../../Shared/homePath';
import AuthPageLayout, {
	AuthHeaderLink,
} from '../../Components/AuthPageLayout';

const Signup = () => {
	const navigate = useNavigate();
	const { dispatch, state } = useAppContext();
	const [signedInEmail, setSignedInEmail] = useState('');
	const [formData, setFormData] = useState({
		email: '',
		password: '',
		confirmPassword: '',
		orgName: '',
		firstName: '',
		lastName: '',
		phone: '',
		acceptTerms: false,
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Sign up' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	useEffect(() => {
		if (state.isAuthenticated && state.user?.email) {
			setSignedInEmail(state.user.email);
		}
	}, [state.isAuthenticated, state.user?.email]);

	const clearExistingSession = () => {
		clearLocalAuth();
		dispatch({ type: ACTION_TYPES.LOGGED_OUT });
		setSignedInEmail('');
	};

	const handleChange = (event) => {
		const { name, value, checked, type } = event.target;
		setFormData((current) => ({
			...current,
			[name]: type === 'checkbox' ? checked : value,
		}));
		setError('');
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');

		if (formData.password !== formData.confirmPassword) {
			setError('Passwords do not match.');
			return;
		}
		if (!formData.acceptTerms) {
			setError('Accept the Terms of Service and Privacy Policy to continue.');
			return;
		}

		setLoading(true);
		try {
			clearExistingSession();
			const authResponse = await signupUser(
				{
					email: formData.email.trim(),
					password: formData.password,
					orgName: formData.orgName.trim(),
					firstName: formData.firstName.trim(),
					lastName: formData.lastName.trim(),
					phone: formData.phone.trim(),
					acceptTerms: formData.acceptTerms,
				},
				dispatch,
			);
			navigate(getAuthenticatedHomePath(authResponse.user), { replace: true });
		} catch (err) {
			const message =
				err?.data?.email?.[0] ||
				err?.data?.password?.[0] ||
				err?.data?.org_name?.[0] ||
				err?.data?.first_name?.[0] ||
				err?.data?.last_name?.[0] ||
				err?.data?.phone?.[0] ||
				err?.data?.accept_terms?.[0] ||
				err?.data?.error ||
				err?.data?.detail ||
				err?.message ||
				'Signup failed. Please try again.';
			setError(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout
			alignContent="start"
			dense
			lockScroll
			headerRight={<AuthHeaderLink to="/login">Sign in</AuthHeaderLink>}
		>
			{signedInEmail && (
				<Alert
					severity="info"
					sx={{ mb: 1.5 }}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => navigate('/jobs')}
						>
							Go to app
						</Button>
					}
				>
					You&apos;re signed in as {signedInEmail}. Continue below to create a
					new organization (we&apos;ll sign you out first), or go back to your
					current account.
				</Alert>
			)}

			<Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 2 }}>
				<Typography
					variant="h5"
					sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.3 }}
				>
					Start your 30-day free trial
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mb: 1.5, lineHeight: 1.6 }}
				>
					No credit card. Create your shop, send proposals, and try bid text
					alerts, scheduling, and analytics.
				</Typography>
				<EmailMockup />
			</Box>

			<Box sx={{ display: { xs: 'none', lg: 'block' }, mb: 1.5 }}>
				<Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
					Create your organization
				</Typography>
				<Typography variant="body2" color="text.secondary">
					30-day free trial · No credit card · Cancel anytime
				</Typography>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 1.5 }}>
					{error}
				</Alert>
			)}

			<Box component="form" onSubmit={handleSubmit}>
				<Stack spacing={1.5}>
					<Paper
						elevation={0}
						sx={{
							p: { xs: 1.5, sm: 2 },
							borderRadius: 2,
							border: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
							Your account
						</Typography>
						<Stack spacing={1.25}>
							<TextField
								label="Company name"
								name="orgName"
								value={formData.orgName}
								onChange={handleChange}
								required
								fullWidth
								size="small"
								autoFocus
							/>
							<TextField
								label="Work email"
								name="email"
								type="email"
								value={formData.email}
								onChange={handleChange}
								required
								fullWidth
								size="small"
								autoComplete="email"
							/>
							<TextField
								label="Mobile phone"
								name="phone"
								type="tel"
								value={formData.phone}
								onChange={handleChange}
								required
								fullWidth
								size="small"
								autoComplete="tel"
							/>
							<Grid container spacing={1.25}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="First name"
										name="firstName"
										value={formData.firstName}
										onChange={handleChange}
										required
										fullWidth
										size="small"
										autoComplete="given-name"
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Last name"
										name="lastName"
										value={formData.lastName}
										onChange={handleChange}
										required
										fullWidth
										size="small"
										autoComplete="family-name"
									/>
								</Grid>
							</Grid>
							<Grid container spacing={1.25}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Password"
										name="password"
										type="password"
										value={formData.password}
										onChange={handleChange}
										required
										fullWidth
										size="small"
										autoComplete="new-password"
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Confirm password"
										name="confirmPassword"
										type="password"
										value={formData.confirmPassword}
										onChange={handleChange}
										required
										fullWidth
										size="small"
										autoComplete="new-password"
									/>
								</Grid>
							</Grid>
						</Stack>
					</Paper>

					<FormControlLabel
						sx={{ alignItems: 'flex-start', mx: 0 }}
						control={
							<Checkbox
								name="acceptTerms"
								checked={formData.acceptTerms}
								onChange={handleChange}
								size="small"
								sx={{ pt: 0.25 }}
							/>
						}
						label={
							<Typography variant="caption" color="text.secondary">
								I agree to the{' '}
								<Link to="/terms" target="_blank" rel="noopener noreferrer">
									Terms of Service
								</Link>{' '}
								and{' '}
								<Link to="/privacy" target="_blank" rel="noopener noreferrer">
									Privacy Policy
								</Link>
								.
							</Typography>
						}
					/>

					<Button
						type="submit"
						variant="contained"
						color="warning"
						size="medium"
						disabled={loading}
						fullWidth
						sx={{ py: 1 }}
					>
						{loading ? 'Creating your account...' : 'Start free trial'}
					</Button>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ textAlign: 'center', display: 'block' }}
					>
						No credit card required.
					</Typography>
				</Stack>
			</Box>
		</AuthPageLayout>
	);
};

export default Signup;
